import type { SxProps, Theme } from '@mui/material'

/**
 * Responsive sx templates — bám pattern hiện có trong codebase đã chuẩn:
 *   - skill `/convert-form` (form dialog 2-col)
 *   - `GenericSummaryMap.tsx:175` (card grid progressive)
 *   - `LoginPage.tsx:62,96` (flex + grid 2-pane)
 *   - `Layout.tsx:55` (sidebar Drawer responsive)
 *
 * Mục đích: page mới import const sẵn thay vì tự define `gridTemplateColumns`
 * khác nhau → consistent, mobile-first, không lệch breakpoint.
 *
 * Naming convention:
 *   FORM_*   : form dialog grid
 *   CARD_*   : card/tile grid (dashboard, summary, hub)
 *   PANE_*   : 2-pane (sidebar + main)
 *   WIDE_*   : tables/reports nhiều cột (scroll ngang chấp nhận)
 *   TOOLBAR_*: toolbar/filterbar (luôn wrap)
 *   STACK_*  : flex direction responsive (Login pattern)
 */

// ──────────────────────────────────────────────────────────────────────────────
// FORM DIALOG GRIDS — mobile stack 1-col, desktop multi-col
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Form dialog 2-col (Access spec default).
 * Mobile: 1 col stack; sm+: 2 col.
 * Pattern này đã dùng ở 10+ Factory FormDialog + Settings + LocalP base.
 *
 * Usage:
 *   <Box sx={FORM_GRID_2COL}>
 *     <TextField sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }} />
 *     <TextField sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }} />
 *   </Box>
 */
export const FORM_GRID_2COL: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5,
  rowGap: 1.25,
  alignItems: 'start',
}

/**
 * Form dialog 3-col (rare — form rất rộng vd LocalP Purchase Contract).
 * Mobile: 1 col; sm: 2 col; md+: 3 col.
 */
export const FORM_GRID_3COL: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
  columnGap: 1.5,
  rowGap: 1.25,
  alignItems: 'start',
}

// ──────────────────────────────────────────────────────────────────────────────
// CARD/TILE GRIDS — dashboard, summary, factory data hub
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Card grid 4-up dashboard (Factory data hub, top-row stats).
 * Mobile: 2 col (compact tiles); md+: 4 col.
 */
export const CARD_GRID_4UP: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  gap: 1,
}

/**
 * Card grid progressive 2-3-4 (GenericSummaryMap pattern).
 * Mobile: 2 col; sm: 3 col; md+: 4 col. Dùng cho cell grid nhiều ô nhỏ.
 */
export const CARD_GRID_PROGRESSIVE: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',
    sm: 'repeat(3, 1fr)',
    md: 'repeat(4, 1fr)',
  },
  gap: 0.5,
}

/**
 * Card grid 3-up (HomePage stats row).
 * Mobile: 1 col stack; md+: 3 col equal.
 */
export const CARD_GRID_3UP: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
  gap: 1.5,
}

// ──────────────────────────────────────────────────────────────────────────────
// 2-PANE LAYOUTS — sidebar + main panel
// ──────────────────────────────────────────────────────────────────────────────

/**
 * 2-pane 8:2 (vd FactoryBaoLocSummaryMap: main bảng kho + side panel info).
 * Mobile stack vertical; md+: 8fr : 2fr ratio.
 */
export const PANE_8_2: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '8fr 2fr' },
  gap: 1.5,
}

/**
 * 2-pane equal (LoginPage hero+form).
 * Mobile stack vertical; md+: 1.12fr : 0.88fr (ưu tiên hero slightly wider).
 */
export const PANE_HERO_FORM: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1.12fr 0.88fr' },
  gap: { xs: 3, md: 5 },
}

// ──────────────────────────────────────────────────────────────────────────────
// WIDE TABLE/REPORT — chấp nhận scroll ngang trên mobile
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Wrapper cho table/report nhiều cột cứng (vd Futures Margin Report 17 cột,
 * Factory Summary Map sơ đồ kho). Inner content tự đặt minWidth phù hợp.
 *
 * Usage:
 *   <Box sx={WIDE_SCROLL_WRAPPER}>
 *     <Box sx={{ minWidth: 1200, display:'grid', gridTemplateColumns:'...' }}>
 *       ...
 *     </Box>
 *   </Box>
 *
 * KHÔNG dùng `width: 'max(100%, 1460px)'` — buộc viewport scroll cả page.
 * Dùng pattern này để chỉ table scroll ngang, không kéo theo header/sidebar.
 */
export const WIDE_SCROLL_WRAPPER: SxProps<Theme> = {
  width: '100%',
  overflowX: 'auto',
}

/**
 * Print Preview dialog Paper width.
 * Print Preview hiển thị 1 trang A4 (~794px). Trước đây dùng `maxWidth="xl"`
 * nên trên màn lớn dialog kéo gần hết bề ngang → dư khoảng trắng 2 bên trang.
 * Constant này cap còn ~6/10 viewport ở màn lớn (lg+) nhưng giữ floor 900px
 * để khổ A4 vẫn nằm gọn, không scroll ngang. Mobile/tablet vẫn full bề ngang.
 *
 * Usage (thay cho `maxWidth="xl"`):
 *   <Dialog maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
 */
export const PRINT_PREVIEW_DIALOG_PAPER: SxProps<Theme> = {
  width: { xs: '100%', md: '92vw', lg: 'clamp(900px, 60vw, 1200px)' },
  maxWidth: { xs: '100%', md: '92vw', lg: 'clamp(900px, 60vw, 1200px)' },
}

// ──────────────────────────────────────────────────────────────────────────────
// TOOLBAR / FILTERBAR — luôn wrap khi overflow
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Toolbar/FilterBar pattern: row trên desktop, column trên mobile.
 * Mỗi child có thể có minWidth riêng để tránh squeeze.
 */
export const TOOLBAR_WRAP: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  flexWrap: 'wrap',
  gap: 1,
  alignItems: { xs: 'stretch', sm: 'center' },
  rowGap: 1,
}

/**
 * Action button group (Save/Cancel/Delete...) — wrap khi mobile có nhiều nút.
 */
export const ACTION_BUTTONS_WRAP: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 0.75,
  '& > button': { minWidth: { xs: 'auto', sm: 80 } },
}

// ──────────────────────────────────────────────────────────────────────────────
// FLEX STACK — vd hero section login
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Flex stack đảo chiều: column mobile, row md+ (LoginPage hero pattern).
 */
export const STACK_COL_TO_ROW: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'center',
  gap: { xs: 3, md: 5, lg: 7 },
}

// ──────────────────────────────────────────────────────────────────────────────
// COMMON PADDING — page container responsive
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Page container padding — compact mobile, generous desktop.
 * Dùng cho `<Container>`, `<Box>` root của page.
 */
export const PAGE_PADDING: SxProps<Theme> = {
  px: { xs: 1.5, sm: 2, md: 3 },
  py: { xs: 1.5, md: 2 },
}

/**
 * Dialog content padding — match `<DialogContent>` MUI nhưng responsive.
 */
export const DIALOG_CONTENT_PADDING: SxProps<Theme> = {
  px: { xs: 1.5, sm: 2.5 },
  py: { xs: 1.25, sm: 1.75 },
}

// ──────────────────────────────────────────────────────────────────────────────
// TEXT VISIBILITY HELPERS — hide / truncate cho mobile
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Truncate text dài + tooltip — dùng cho DialogTitle, PageHeader, label dài.
 *
 * Combo với prop `noWrap` của Typography + `title={text}` để hover/long-press
 * show full. KHÔNG dùng cho text ngắn (gây ellipsis vô lý).
 *
 * Usage:
 *   <Typography noWrap title={title} sx={{ ...TEXT_TRUNCATE, flex: 1 }}>
 *     {title}
 *   </Typography>
 */
export const TEXT_TRUNCATE: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

/**
 * Hide on mobile (xs < 600px), show on sm+ — block element.
 * Dùng cho: helper text giải thích, subtitle PageHeader, label phụ
 * ("Snapshot Date:" trước DateTextField), Total/Loaded count trong toolbar.
 *
 * KHÔNG dùng cho validation error helperText — error phải luôn show.
 */
export const HIDE_ON_MOBILE: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
}

/**
 * Hide on mobile (xs < 600px), show on sm+ — inline element.
 * Dùng cho button text wrap với startIcon (icon-only mobile pattern), span con
 * trong Typography (module prefix DialogTitle).
 *
 * Pattern button icon-only mobile:
 *   <Button startIcon={<Icon sx={{fontSize:16}}/>} title="New">
 *     <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
 *   </Button>
 */
export const HIDE_ON_MOBILE_INLINE: SxProps<Theme> = {
  display: { xs: 'none', sm: 'inline' },
}

/**
 * Show ONLY on mobile (xs < 600px), hide on sm+ — block.
 * Inverse of HIDE_ON_MOBILE. Dùng khi mobile có version compact riêng.
 */
export const SHOW_ON_MOBILE: SxProps<Theme> = {
  display: { xs: 'block', sm: 'none' },
}

/**
 * Hide on mobile + tablet (xs+sm < 900px), show on md+ — inline.
 * Dùng cho button text dài (Change Password, Valid/Invalid Account) — icon-only
 * trên cả mobile và tablet, full text desktop.
 *
 * Pattern progressive disclosure 3-level dùng kèm với SHOW_ON_TABLET_INLINE:
 *   - xs: icon only (cả 2 span ẩn)
 *   - sm: short text via SHOW_ON_TABLET_INLINE
 *   - md+: full text via HIDE_ON_TABLET_INLINE
 *   See Pattern 8 trong skill /responsive-style.
 */
export const HIDE_ON_TABLET_INLINE: SxProps<Theme> = {
  display: { xs: 'none', md: 'inline' },
}

/**
 * Show ONLY on tablet (sm only, 600-900px), hide xs + md+ — inline.
 * Pair với `HIDE_ON_TABLET_INLINE` để làm 3-level progressive disclosure
 * (xs icon-only → sm short text → md+ full text).
 */
export const SHOW_ON_TABLET_INLINE: SxProps<Theme> = {
  display: { xs: 'none', sm: 'inline', md: 'none' },
}

// ──────────────────────────────────────────────────────────────────────────────
// STACK DIRECTION RESPONSIVE — column mobile, row desktop
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Stack đảo chiều: column trên mobile, row trên sm+.
 * Pattern dùng cho permission row (label + RadioGroup), form section header
 * (title + action), label + input pairs.
 *
 * Spread vào props của `<Stack>` (KHÔNG vào sx — direction là Stack prop):
 *   <Stack {...STACK_RESPONSIVE_DIRECTION_PROPS}>...</Stack>
 *
 * Hoặc inline:
 *   <Stack
 *     direction={{ xs: 'column', sm: 'row' }}
 *     alignItems={{ xs: 'flex-start', sm: 'center' }}
 *     spacing={{ xs: 0.25, sm: 1 }}
 *   >
 */
export const STACK_RESPONSIVE_DIRECTION_PROPS = {
  direction: { xs: 'column' as const, sm: 'row' as const },
  alignItems: { xs: 'flex-start' as const, sm: 'center' as const },
  spacing: { xs: 0.25, sm: 1 },
}

// ──────────────────────────────────────────────────────────────────────────────
// TABLE PAGINATION COMPACT — dense font + spacing trên xs
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho `<TablePagination>` để compact trên mobile.
 *
 * Override theme global (16px !important fix iOS zoom) bằng specificity cao hơn
 * trên select (đã có ở `theme/index.ts` CssBaseline `.MuiTablePagination-root
 * .MuiTablePagination-select { 0.65rem !important }`).
 *
 * Usage:
 *   <TablePagination ... sx={TABLE_PAGINATION_COMPACT} />
 *
 * Pattern này thay cho sx flat 0.72rem cũ — kế thừa theme override cho xs.
 */
export const TABLE_PAGINATION_COMPACT: SxProps<Theme> = {
  flexShrink: 0,
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 28, sm: 32 },
    px: { xs: 0.25, sm: 0.5 },
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.65rem', sm: '0.72rem' },
  },
  '& .MuiTablePagination-select': {
    fontSize: { xs: '0.65rem', sm: '0.72rem' },
  },
  '& .MuiTablePagination-actions': {
    transform: { xs: 'scale(0.8)', sm: 'scale(0.92)' },
    transformOrigin: 'center',
  },
}

// ──────────────────────────────────────────────────────────────────────────────
// COMBOBOX FILTER COMPACT — toolbar Autocomplete picker dense mobile
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho `<Autocomplete>` filter selector trong toolbar (vd "Go to Page",
 * "Active Period" — References page nav, page-level filter combobox).
 *
 * iOS Safari zoom fix + visually compact (cả 2):
 *   - `<input>` element giữ 16px (global CssBaseline rule áp dụng — iOS
 *     không trigger zoom)
 *   - Toàn box scale(0.82) visually shrink trên xs → user thấy combobox
 *     nhỏ ~18% mà iOS vẫn nhìn input như 16px.
 *
 * Trade-off transform scale: layout box vẫn 100% size (bounding box không
 * shrink theo transform), nên có khoảng trắng bên cạnh. Dùng
 * `transformOrigin: 'right top'` để empty space rơi về bên trái — kết hợp với
 * `ml: 'auto'` trên Stack chứa Autocomplete (anchor sát phải parent) → trên
 * mobile combobox sát phải form, không "trôi giữa".
 *
 * Usage cho navigation picker (vd "Go to Page" References) — KHÔNG bung
 * keyboard trên mobile vì user chỉ pick từ dropdown:
 *
 *   <Stack sx={{ ml: 'auto' }}>     // anchor sát phải
 *     <Autocomplete
 *       sx={COMBOBOX_FILTER_COMPACT}
 *       disableClearable
 *       renderInput={(params) => (
 *         <TextField
 *           {...params}
 *           label="Go to Page"
 *           InputLabelProps={{ shrink: true }}
 *           // readOnly: mobile không bung keyboard cho navigation picker
 *           inputProps={{ ...params.inputProps, readOnly: true }}
 *         />
 *       )}
 *     />
 *   </Stack>
 *
 * `readOnly` vẫn cho Autocomplete dropdown mở qua click — chỉ block keyboard
 * popup khi tap. Áp dụng cho navigation picker; KHÔNG áp dụng combobox cần
 * typeahead search (user gõ filter list).
 */
export const COMBOBOX_FILTER_COMPACT: SxProps<Theme> = {
  minWidth: { xs: 170, sm: 240 },
  // xs: scale visually nhỏ ~18% mà KHÔNG đụng font-size <input> → no iOS zoom
  transform: { xs: 'scale(0.82)', sm: 'none' },
  transformOrigin: { xs: 'right top', sm: 'unset' },
  '& .MuiInputBase-root': {
    minHeight: 32,
    fontSize: { sm: '0.8rem' },
  },
  '& .MuiInputBase-input': {
    py: 0.5,
    // xs: KHÔNG override fontSize — global rule (16px !important) áp dụng
    // → iOS Safari thấy input font = 16px → no auto-zoom on tap
    fontSize: { sm: '0.8rem' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { sm: '0.8rem' },
  },
}

// ──────────────────────────────────────────────────────────────────────────────
// DATE FIELD COMPACT — override iOS 16px rule cho input dense
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho DateTextField (hoặc TextField input dense khác) để override global
 * 16px !important fix trên mobile. Dùng khi cần input thật sự dense (toolbar
 * filter, inline edit, ...) và chấp nhận iOS zoom nhẹ khi tap.
 *
 * KHÔNG dùng cho form input chính — để global rule áp dụng tránh iOS zoom.
 *
 * Usage:
 *   <DateTextField sx={DATE_FILTER_COMPACT} ... />
 */
export const DATE_FILTER_COMPACT: SxProps<Theme> = {
  width: { xs: 130, sm: 150 },
  '& .MuiInputBase-root': {
    fontSize: { xs: '0.85rem', sm: '0.8rem' },
  },
  '& .MuiInputBase-input': {
    py: { xs: 0.5, sm: 0.625 },
    // !important override theme CssBaseline global rule (16px @media xs)
    fontSize: { xs: '0.85rem !important', sm: '0.8rem !important' },
  },
}

// ──────────────────────────────────────────────────────────────────────────────
// FILTER CHUNG HEADER — Page-level filter (DatePicker, Select trong PageHeader)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho filter chung trong PageHeader (vd Shipment Period DatePicker, Is
 * Finalized Select ở SaleContractPage). Compact tối đa: height 26px xs,
 * font 0.7rem !important override global 16px iOS rule.
 *
 * Dùng kèm pattern "label inside instead of outside":
 *   - KHÔNG dùng Typography "Shipment Period:" bên ngoài
 *   - Dùng `label="Shipment From"` + `InputLabelProps={{shrink:true}}` inline
 *
 * Lý do: label inline tiết kiệm 1 dòng + scoped với input + accessible.
 */
export const FILTER_HEADER_INPUT_SX: SxProps<Theme> = {
  width: { xs: 130, sm: 158 },
  '& .MuiInputBase-root': {
    minHeight: { xs: 26, sm: 26 },
    fontSize: { xs: '0.7rem', sm: '0.72rem' },
  },
  '& .MuiInputBase-input': {
    py: { xs: 0.15, sm: 0.2 },
    px: 0.5,
    fontSize: { xs: '0.7rem !important', sm: '0.72rem !important' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.68rem', sm: '0.7rem' },
  },
}

/**
 * sx cho Select filter chung trong PageHeader. Cùng pattern
 * FILTER_HEADER_INPUT_SX nhưng width nhỏ hơn (102/110) và áp `.MuiSelect-select`
 * thay `.MuiInputBase-input`.
 */
export const FILTER_HEADER_SELECT_SX: SxProps<Theme> = {
  width: { xs: 102, sm: 110 },
  '& .MuiInputBase-root': {
    minHeight: { xs: 26, sm: 26 },
    fontSize: { xs: '0.7rem', sm: '0.72rem' },
  },
  '& .MuiSelect-select': {
    py: { xs: 0.15, sm: 0.2 },
    px: 0.5,
    fontSize: { xs: '0.7rem !important', sm: '0.72rem !important' },
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.68rem', sm: '0.7rem' },
  },
}

// ──────────────────────────────────────────────────────────────────────────────
// CARDCONTENT COMPACT — padding responsive cho CardContent wrap data table
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho `<CardContent>` wrap data table. MUI default p:24px (cả 4 cạnh) +
 * pb:32 ở last-child. Mobile → 8px compact để tăng không gian table.
 *
 * Usage:
 *   <CardContent sx={CARD_CONTENT_COMPACT}>...</CardContent>
 */
export const CARD_CONTENT_COMPACT: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  pt: { xs: 1, sm: 2 },
  px: { xs: 1, sm: 2 },
  pb: { xs: 1, sm: 2 },
  '&:last-child': { pb: { xs: 1, sm: 2 } },
}

// ──────────────────────────────────────────────────────────────────────────────
// TABLE INNER HEIGHT — wrap TableVirtuoso với height responsive
// ──────────────────────────────────────────────────────────────────────────────

/**
 * sx cho Box wrap `<TableVirtuoso>` bên trong `.app-dataframe`. Mobile xs:
 * `height: 'auto'` để table flex-grow theo parent. Desktop: fixed height N (px).
 *
 * Khi parent là `.app-dataframe` (có clamp height), inner Box height:auto OK.
 * Override `height: N` cứng trên xs sẽ làm table bị crop ngắn lại.
 *
 * Usage:
 *   <Box sx={tableInnerHeight(620)}>
 *     <TableVirtuoso ... />
 *   </Box>
 */
export function tableInnerHeight(desktopPx: number): SxProps<Theme> {
  return {
    flex: 1,
    minHeight: { xs: 280, sm: 320 },
    height: { xs: 'auto', sm: desktopPx },
    overflow: 'hidden',
  }
}
